/**
 * Backend/runtime configuration. ONE backend-mode gate (docs/07 §4): a single exported
 * constant decides real backend vs. emulators. Per-module flags are forbidden.
 */
import type { FirebaseOptions } from 'firebase/app';

/** Region every Cloud Function is pinned to (docs/03 §2). Never the default us-central1. */
export const FUNCTIONS_REGION = 'me-central1';

/** The single backend-mode gate. */
export const useEmulators = import.meta.env.VITE_USE_EMULATORS === 'true';

export const firebaseConfig: FirebaseOptions = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

/** Idle auto-lock for this privileged desk tool, in minutes (docs/05 session handling). */
export const idleLockMinutes = Number(import.meta.env.VITE_IDLE_LOCK_MINUTES ?? '20') || 20;

/** Emulator host/ports (docs/09 §3.1). Always 127.0.0.1, never localhost (docs/09 §3.2). */
export const emulatorHosts = {
  host: '127.0.0.1',
  auth: 'http://127.0.0.1:9099',
  firestore: { host: '127.0.0.1', port: 8080 },
  functions: { host: '127.0.0.1', port: 5001 },
  storage: { host: '127.0.0.1', port: 9199 },
} as const;

/**
 * Which half of the dashboard this build is (spec 2026-09-21 host split): the super-admin panel
 * served at admin.aabar.io, or the therapist / centre-admin portal at center.aabar.io. A build-time
 * constant — the router imports only this surface's routes, so each site ships only its own code.
 */
export type AppSurface = 'admin' | 'portal';
export function parseSurface(raw: string | undefined): AppSurface {
  return raw === 'portal' ? 'portal' : 'admin';
}
export const appSurface: AppSurface = parseSurface(import.meta.env.VITE_APP_SURFACE);

/** Absolute origins for the cross-site links ("this account uses center.aabar.io"). */
export const adminOrigin: string = import.meta.env.VITE_ADMIN_ORIGIN || 'https://admin.aabar.io';
export const portalOrigin: string = import.meta.env.VITE_PORTAL_ORIGIN || 'https://center.aabar.io';
